import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Mail, Shield, Users, BarChart3, Brain, ShieldCheck, HelpCircle, Quote } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { api } from '../utils/api';

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [mockOTP, setMockOTP] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.login(email, password);
      toast.success('Login successful!');
      onLogin(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.forgotPassword(forgotEmail);
      setMockOTP(response.data.mock_otp);
      toast.success('Reset link sent! Check your email.');
      setShowForgotPassword(false);
      setShowOTPModal(true);
    } catch (error) {
      toast.error('Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.verifyOTP(forgotEmail, otp);
      toast.success('OTP verified! You can now reset your password.');
      setShowOTPModal(false);
      setOtp('');
    } catch (error) {
      toast.error('Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Dark branded hero */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden" style={{ background: 'linear-gradient(160deg, #1a0533 0%, #2d1052 40%, #4a1a7a 70%, #6b21a8 100%)' }}>
        {/* Gradient overlay */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 80% 20%, rgba(107, 33, 168, 0.3) 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(88, 28, 135, 0.2) 0%, transparent 50%)'
        }} />
        {/* Subtle mesh texture */}
        <div className="absolute top-0 right-0 w-1/2 h-1/2 opacity-20" style={{
          background: 'radial-gradient(circle at 70% 30%, rgba(147, 51, 234, 0.3) 0%, transparent 50%)'
        }} />

        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          {/* Top: Logo + Badge */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <img src="/favicon.svg" alt="HealthLink360" className="w-10 h-10" />
              <span className="text-white text-2xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>HealthLink360</span>
            </div>
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-purple-700/40 bg-purple-900/20 mb-8">
              <ShieldCheck className="w-4 h-4 text-purple-300 mr-2" />
              <span className="text-purple-200 text-xs font-semibold tracking-widest uppercase">Admin Command Center</span>
            </div>

            {/* Tagline */}
            <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Smarter care.<br />Stronger outcomes.
            </h1>
            <p className="text-gray-400 text-base leading-relaxed max-w-md mb-10">
              Monitor engagement, analyze insights, and manage care operations that improve lives and lower costs.
            </p>

            {/* Feature Grid */}
            <div className="grid grid-cols-4 gap-4 mb-10">
              {[
                { icon: <BarChart3 className="w-6 h-6" />, title: 'Real-time Dashboards', desc: 'Track engagement and outcomes' },
                { icon: <Users className="w-6 h-6" />, title: 'Care Management', desc: 'Route, assign, and coordinate care' },
                { icon: <Brain className="w-6 h-6" />, title: 'AI-Powered Insights', desc: 'Identify risks and opportunities' },
                { icon: <ShieldCheck className="w-6 h-6" />, title: 'Secure & Compliant', desc: 'HIPAA-conscious and role-based' },
              ].map((feat, i) => (
                <div key={i} className="text-left">
                  <div className="text-purple-300 mb-2">{feat.icon}</div>
                  <p className="text-white text-sm font-semibold leading-tight">{feat.title}</p>
                  <p className="text-gray-500 text-xs mt-1 leading-snug">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom: Dashboard preview + Quote */}
          <div className="flex items-end gap-4">
            {/* Dashboard preview card */}
            <div className="flex-1 rounded-xl overflow-hidden border border-gray-800 bg-gray-900/80 backdrop-blur-sm shadow-2xl">
              <div className="px-4 py-3 border-b border-gray-800 flex items-center gap-2">
                <img src="/favicon.svg" alt="" className="w-4 h-4" />
                <span className="text-white text-xs font-medium">HealthLink360</span>
                <span className="text-gray-600 text-xs ml-2">Dashboard</span>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="bg-gray-800/60 rounded-lg p-2.5">
                    <p className="text-gray-500 text-[10px]">Total Members</p>
                    <p className="text-white text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>24,875</p>
                    <p className="text-green-500 text-[10px]">+12% vs last month</p>
                  </div>
                  <div className="bg-gray-800/60 rounded-lg p-2.5">
                    <p className="text-gray-500 text-[10px]">Engagement Score</p>
                    <p className="text-white text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>78%</p>
                    <p className="text-green-500 text-[10px]">+8% vs last month</p>
                  </div>
                  <div className="bg-gray-800/60 rounded-lg p-2.5">
                    <p className="text-gray-500 text-[10px]">Care Actions</p>
                    <p className="text-white text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>1,452</p>
                    <p className="text-green-500 text-[10px]">+19% vs last month</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-800/40 rounded-lg p-2 h-16 flex items-end gap-[2px]">
                    {[35, 48, 42, 55, 50, 62, 58, 70, 65, 72, 68, 75].map((h, i) => (
                      <div key={i} className="flex-1 rounded-t" style={{ height: `${h}%`, background: `linear-gradient(to top, #581c87, #9333ea)` }} />
                    ))}
                  </div>
                  <div className="w-16 h-16 relative">
                    <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                      <circle cx="18" cy="18" r="14" fill="none" stroke="#374151" strokeWidth="3" />
                      <circle cx="18" cy="18" r="14" fill="none" stroke="#dc2626" strokeWidth="3" strokeDasharray="88" strokeDashoffset="60" strokeLinecap="round" />
                      <circle cx="18" cy="18" r="14" fill="none" stroke="#f97316" strokeWidth="3" strokeDasharray="88" strokeDashoffset="75" strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white text-[9px] font-bold">32%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quote card */}
            <div className="w-48 shrink-0">
              <div className="rounded-xl p-4" style={{ background: 'linear-gradient(135deg, rgba(88, 28, 135, 0.5) 0%, rgba(59, 18, 90, 0.6) 100%)', border: '1px solid rgba(147, 51, 234, 0.2)' }}>
                <div className="w-7 h-7 rounded-full bg-purple-900/60 flex items-center justify-center mb-2">
                  <Quote className="w-3.5 h-3.5 text-purple-300" />
                </div>
                <p className="text-white text-sm leading-snug">
                  Food is medicine.<br />
                  Data is insight.<br />
                  <span className="text-purple-300">We connect the two.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Sign in form */}
      <div className="flex-1 flex flex-col bg-gray-50 min-h-screen">
        {/* Top right help link */}
        <div className="flex justify-end px-8 pt-5">
          <a href="/help" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-purple-700 transition-colors" data-testid="contact-support-link">
            <span>Need help?</span>
            <HelpCircle className="w-4 h-4" />
            <span className="text-purple-700 font-medium">Contact Support</span>
          </a>
        </div>

        {/* Centered form */}
        <div className="flex-1 flex items-center justify-center px-8">
          <div className="w-full max-w-md">
            {/* Mobile-only branding */}
            <div className="lg:hidden text-center mb-8">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <img src="/favicon.svg" alt="HealthLink360" className="w-10 h-10" />
                <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>HealthLink360</h1>
              </div>
              <p className="text-gray-500 text-sm">Admin Command Center</p>
            </div>

            {/* Welcome heading */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Welcome back, <span className="text-purple-700">Admin</span>
              </h2>
              <p className="text-gray-500 text-sm mt-2">Sign in to your HealthLink360 AI Admin Command Center</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@healthlink360.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-11 h-12 bg-white border-gray-200 rounded-xl"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-11 pr-11 h-12 bg-white border-gray-200 rounded-xl"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="mr-2 rounded border-gray-300 text-purple-700 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-600">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm font-medium text-purple-700 hover:text-purple-800"
                  data-testid="forgot-password-btn"
                >
                  Forgot password?
                </button>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-xl text-sm font-semibold text-white"
                style={{ background: 'linear-gradient(135deg, #6b21a8 0%, #7c3aed 100%)' }}
                disabled={loading}
                data-testid="login-submit-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            {/* SSO divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-gray-50 text-gray-400">Or continue with</span>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full h-12 rounded-xl border-gray-200 text-gray-700 font-medium"
              onClick={() => toast.info('SSO integration coming soon')}
              data-testid="sso-btn"
            >
              <ShieldCheck className="w-4 h-4 mr-2 text-purple-700" />
              Single Sign-On (SSO)
            </Button>

            {/* Trust markers */}
            <div className="flex items-center justify-center gap-6 mt-8">
              {[
                { icon: <Shield className="w-5 h-5" />, label: 'Secure Access', desc: '256-bit encryption' },
                { icon: <Users className="w-5 h-5" />, label: 'Role-Based', desc: 'Permissions' },
                { icon: <ShieldCheck className="w-5 h-5" />, label: 'HIPAA-Conscious', desc: 'Your data is protected' },
              ].map((marker, i) => (
                <div key={i} className="flex items-center gap-2 text-center">
                  <div className="text-purple-700">{marker.icon}</div>
                  <div className="text-left">
                    <p className="text-xs font-semibold text-gray-700">{marker.label}</p>
                    <p className="text-[10px] text-gray-400">{marker.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Demo credentials */}
            <div className="text-center mt-4">
              <p className="text-xs text-gray-400">Demo Credentials: admin@healthlink360.com / admin123</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 pb-5 flex items-center justify-center gap-4 text-xs text-gray-400">
          <span>&copy; 2025 HealthLink360 AI. All rights reserved.</span>
          <span>Privacy Policy</span>
          <span>|</span>
          <span>Terms of Service</span>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent data-testid="forgot-password-modal">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="Enter your email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                data-testid="forgot-email-input"
              />
            </div>
            <Button type="submit" className="w-full" style={{ background: '#6b21a8' }} disabled={loading} data-testid="send-reset-link-btn">
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* OTP Verification Modal */}
      <Dialog open={showOTPModal} onOpenChange={setShowOTPModal}>
        <DialogContent data-testid="otp-modal">
          <DialogHeader>
            <DialogTitle>Verify OTP</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleVerifyOTP} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="otp">Enter OTP</Label>
              <Input
                id="otp"
                type="text"
                placeholder="Enter 6-digit code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
                required
                data-testid="otp-input"
              />
              {mockOTP && (
                <p className="text-sm text-gray-500" data-testid="mock-otp-display">
                  Mock OTP: <span className="font-mono font-bold">{mockOTP}</span>
                </p>
              )}
            </div>
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1" style={{ background: '#6b21a8' }} disabled={loading} data-testid="verify-otp-btn">
                {loading ? 'Verifying...' : 'Verify'}
              </Button>
              <Button type="button" variant="outline" onClick={handleForgotPassword} disabled={loading} data-testid="resend-otp-btn">
                Resend
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
